"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArtworkCard, { type ArtworkCardLinks } from "./ArtworkCard";
import type { Artwork } from "./artwork-types";

export interface ArtworkRowProps {
  title: string;
  artworks: Artwork[];
  card: (artwork: Artwork) => ArtworkCardLinks;
}

/**
 * A titled shelf of works that scrolls sideways.
 *
 * Sideways so it costs one row of height however many works it holds — on a
 * phone the grid below is what the customer came for. The cards are the
 * grid's own, at a width that always leaves part of the next one showing:
 * the cut-off card is the sign that there is more, so it is cut on purpose
 * and by the same amount every time, not wherever the screen happens to end.
 * Desktops get arrows, because a mouse has no sideways swipe.
 */
export default function ArtworkRow({ title, artworks, card }: ArtworkRowProps) {
  const t = useTranslations("art");
  const scroller = useRef<HTMLDivElement | null>(null);
  const [edges, setEdges] = useState({ start: true, end: false });

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    setEdges({
      start: el.scrollLeft <= 2,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = scroller.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, artworks.length]);

  const page = (direction: 1 | -1) => {
    const el = scroller.current;
    if (!el) return;
    // Most of a screen, so the card that was peeking becomes the first one.
    el.scrollBy({ left: direction * el.clientWidth * 0.85, behavior: "smooth" });
  };

  if (!artworks.length) return null;

  const arrowSx = {
    border: 1,
    borderColor: "divider",
    width: 40,
    height: 40,
  } as const;

  return (
    <Box component="section" sx={{ mb: { xs: 4, md: 5 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ display: { xs: "none", md: "flex" } }}>
          <IconButton
            aria-label={t("scrollPrevious")}
            onClick={() => page(-1)}
            disabled={edges.start}
            sx={arrowSx}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            aria-label={t("scrollNext")}
            onClick={() => page(1)}
            disabled={edges.end}
            sx={arrowSx}
          >
            <ChevronRightIcon />
          </IconButton>
        </Stack>
      </Stack>
      <Box
        ref={scroller}
        onScroll={measure}
        sx={{
          display: "flex",
          gap: { xs: 1.5, md: 3 },
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollPaddingLeft: { xs: 16, md: 0 },
          // Bleeds to the screen edge on a phone, so the peeking card is cut
          // by the screen and not by the page margin.
          mx: { xs: -2, sm: -3, md: 0 },
          px: { xs: 2, sm: 3, md: 0 },
          pb: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {artworks.map((artwork) => (
          <Box
            key={artwork.id}
            sx={{
              // Two and a bit on a phone, three and a bit on a tablet, four
              // and a bit on a desktop — the grid's columns, plus the peek.
              flex: {
                xs: "0 0 calc((100% - 12px) / 2.3)",
                sm: "0 0 calc((100% - 24px) / 3.3)",
                md: "0 0 calc((100% - 72px) / 4.3)",
              },
              minWidth: 0,
              scrollSnapAlign: "start",
            }}
          >
            <ArtworkCard artwork={artwork} {...card(artwork)} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}
